import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Home, Layers, Users, Briefcase, MessageSquare, Phone, Mail, Building2, Globe2 } from 'lucide-react';
import { services, industries } from '../mock/mockData';
import { useLang } from '../contexts/LanguageContext';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import ContinentBar from './ContinentBar';
import RightSideMenu from './RightSideMenu';
import RegionSwitcher from './RegionSwitcher';
import MegaServices from './nav/MegaServices';
import MegaIndustries from './nav/MegaIndustries';
import MegaAbout from './nav/MegaAbout';
import MarketsMenu from './nav/MarketsMenu';
import MobileMenu from './nav/MobileMenu';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(null); // 'services' | 'industries' | 'about' | null
  const location = useLocation();
  const { t } = useLang();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(null);
  }, [location.pathname]);

  const grouped = services.reduce((acc, s) => {
    acc[s.category] = acc[s.category] || [];
    acc[s.category].push(s);
    return acc;
  }, {});

  const openMega = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(null), 180);
  };
  const onEnterMega = (key) => () => openMega(key);

  return (
    <>
      <ContinentBar />

      {/* top bar */}
      <div className="hidden lg:block bg-[#1a1f2e] text-gray-300 text-[12px] border-b border-gray-900/40">
        <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:+17866732523" className="link-underline flex items-center gap-2 hover:text-green-400"><Phone className="w-3.5 h-3.5" /> (+1) 786-673-2523</a>
            <a href="tel:+919960250279" className="link-underline flex items-center gap-2 hover:text-green-400"><Phone className="w-3.5 h-3.5" /> +91 9960 250 279</a>
            <a href="mailto:info@landdrafters.com" className="link-underline flex items-center gap-2 hover:text-green-400"><Mail className="w-3.5 h-3.5" /> info@landdrafters.com</a>
            <a href="mailto:landdrafters@gmail.com" className="link-underline flex items-center gap-2 hover:text-green-400"><Mail className="w-3.5 h-3.5" /> landdrafters@gmail.com</a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-orange-400 font-medium tracking-wide flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" /> Global Engineering Services | 24/7 Support</span>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg shadow-gray-300/40 border-b border-gray-200' : 'bg-white border-b border-gray-100'}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between min-h-[110px] py-3 gap-3">
            <Logo size="md" />

            {/* Desktop nav — visible at lg+ (>=1024px) */}
            <nav className="hidden lg:flex items-center gap-0.5 text-[13px] font-semibold tracking-wider uppercase">
              <NavLink to="/" icon={<Home className="w-4 h-4" />}>{t('home')}</NavLink>
              <MegaTrigger label={t('services')}   to="/services"   icon={<Layers className="w-4 h-4" />}     isOpen={megaOpen === 'services'}   onEnter={onEnterMega('services')}   onLeave={scheduleClose} />
              <MegaTrigger label={t('industries')} to="/industries" icon={<Building2 className="w-4 h-4" />}  isOpen={megaOpen === 'industries'} onEnter={onEnterMega('industries')} onLeave={scheduleClose} />
              <MegaTrigger label={t('about')}      to="/about"      icon={<Users className="w-4 h-4" />}      isOpen={megaOpen === 'about'}      onEnter={onEnterMega('about')}      onLeave={scheduleClose} />
              <NavLink to="/careers" icon={<Briefcase className="w-4 h-4" />}>{t('careers')}</NavLink>
              <NavLink to="/contact" icon={<MessageSquare className="w-4 h-4" />}>{t('contact')}</NavLink>
            </nav>

            {/* Right-side controls — always visible */}
            <div className="flex items-center gap-2 shrink-0">
              <RegionSwitcher compact />
              <LanguageSwitcher compact />
              <div className="hidden sm:flex flex-col items-stretch gap-1.5">
                <MarketsMenu />
                <Link
                  to="/get-quote"
                  data-testid="nav-get-started"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-[11px] md:text-[12px] font-bold px-3 md:px-5 py-2.5 rounded uppercase tracking-wider btn-shine transition-all hover:shadow-lg hover:shadow-orange-500/40 whitespace-nowrap"
                >
                  {t('getStarted')}
                </Link>
              </div>
              <RightSideMenu />
              {/* Mobile menu toggle — visible BELOW lg */}
              <button
                className="lg:hidden text-[#0e1116] hover:text-green-600 p-2"
                onClick={() => setOpen(!open)}
                aria-label="toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {open ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </div>

        {/* FULL-WIDTH MEGA MENUS (desktop) */}
        {megaOpen === 'services' && (
          <MegaServices
            grouped={grouped}
            totalCount={services.length}
            onEnter={() => openMega('services')}
            onLeave={scheduleClose}
          />
        )}

        {megaOpen === 'industries' && (
          <MegaIndustries
            industries={industries}
            onEnter={() => openMega('industries')}
            onLeave={scheduleClose}
          />
        )}

        {megaOpen === 'about' && (
          <MegaAbout
            onEnter={() => openMega('about')}
            onLeave={scheduleClose}
          />
        )}

        {/* mobile menu */}
        {open && <MobileMenu services={services} industries={industries} t={t} />}
      </header>
    </>
  );
};

const NavLink = ({ to, children, icon }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link to={to} className={`nav-link flex items-center gap-1.5 px-3 py-3 whitespace-nowrap transition-colors ${active ? 'text-green-600 nav-active' : 'text-[#0e1116] hover:text-green-600'}`}>
      {icon} {children}
    </Link>
  );
};

const MegaTrigger = ({ label, to, icon, isOpen, onEnter, onLeave }) => {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      aria-expanded={isOpen}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`nav-link flex items-center gap-1.5 px-3 py-3 whitespace-nowrap transition-colors ${(isOpen || active) ? 'text-green-600 nav-active' : 'text-[#0e1116] hover:text-green-600'}`}
    >
      {icon} {label} <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </Link>
  );
};

export default Navbar;
